import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * AdminChangesToggle
 * - Pretty, self-contained React page for toggling the DB change feed (SSE+poll)
 * - Calls: https://shopaholics.ge/api/admin/changes_toggle.php (POST JSON)
 * - Also has a fallback status check using /api/changes_poll.php?cursor=0 (410 => OFF, else => maybe ON)
 * - Uses only browser APIs, no external UI libs.
 *
 * Place it behind your admin route (e.g. at admin.shopaholics.ge/changes) and render <AdminChangesToggle />
 */
export default function AdminChangesToggle() {
  const ENDPOINT = "/api/changes_toggle_proxy.php";
  const POLL_URL = "https://shopaholics.ge/api/changes_poll.php?cursor=0";

  const [status, setStatus] = useState(/** @type {"unknown"|"on"|"off"|"error"} */("unknown"));
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState("");

  const cardColor = useMemo(() => {
    switch (status) {
      case "on": return "#065f46"; // emerald-800
      case "off": return "#7f1d1d"; // red-800
      case "error": return "#7c2d12"; // amber-900
      default: return "#1f2937"; // gray-800
    }
  }, [status]);

  const fetchJSON = useCallback(async (url, init) => {
    const res = await fetch(url, { credentials: "include", ...init });
    const text = await res.text();
    try { return { res, json: JSON.parse(text) }; }
    catch { return { res, json: null, text }; }
  }, []);

  const checkViaToggleEndpoint = useCallback(async () => {
    // Try optional 'status' action first (if backend implements it)
    const { res, json } = await fetchJSON(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "status" }),
    });

    if (res.ok && json && typeof json.enabled === "boolean") {
      return json.enabled ? ("on") : ("off");
    }
    return null; // fall back to poll probe
  }, [ENDPOINT, fetchJSON]);

  const checkViaPollProbe = useCallback(async () => {
    const res = await fetch(POLL_URL, { credentials: "include" });
    if (res.status === 410) return "off"; // disabled by gate
    // 200/204/304/5xx: we optimistically consider it ON (unless 5xx means DB issue)
    return res.ok ? "on" : "error";
  }, [POLL_URL]);

  const readStatus = useCallback(async () => {
    setLoading(true); setNote("");
    try {
      const direct = await checkViaToggleEndpoint();
      if (direct) { setStatus(direct); return; }
      const probe = await checkViaPollProbe();
      setStatus(probe);
    } catch (e) {
      console.error(e);
      setStatus("error");
      setNote(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }, [checkViaPollProbe, checkViaToggleEndpoint]);

  useEffect(() => { readStatus(); }, [readStatus]);

  const sendAction = useCallback(async (action /** 'on'|'off' */) => {
    setLoading(true); setNote("");
    try {
      const { res, json, text } = await fetchJSON(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
      if (json && typeof json.enabled === "boolean") {
        setStatus(json.enabled ? "on" : "off");
      } else {
        // fallback: re-check
        await readStatus();
      }
      setNote(json?.ok === false ? (json?.error || "unknown error") : "");
    } catch (e) {
      console.error(e);
      setStatus("error");
      setNote(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }, [ENDPOINT, fetchJSON, readStatus]);

  const ui = {
    bg: "#0b1220",
    ink: "#e5ecff",
    dim: "#9fb0ce",
    cardBg: "#111827",
    cardBorder: "rgba(255,255,255,.08)",
    line: "rgba(255,255,255,.1)",
    shadow: "0 24px 60px rgba(0,0,0,.35)",
    brand: "#7c3aed",
    brand2: "#22c55e",
    danger: "#ef4444",
  };

  return (
    <div style={{minHeight:"100vh",background:ui.bg,color:ui.ink,display:"grid",placeItems:"center",padding:"32px"}}>
      <div style={{width:"min(860px, 94vw)", background: ui.cardBg, border:"1px solid "+ui.cardBorder, borderRadius: 16, boxShadow: ui.shadow}}>
        <header style={{display:"flex",alignItems:"center",gap:12, padding:"16px 18px", borderBottom:"1px solid "+ui.line}}>
          <div style={{width:36,height:36,borderRadius:10,display:"grid",placeItems:"center",background: cardColor, boxShadow:"inset 0 0 0 1px rgba(255,255,255,.15)"}}>⚙️</div>
          <div>
            <div style={{fontWeight:900, fontSize:18}}>Changes Feed Control</div>
            <div style={{fontSize:12, color:ui.dim}}>Toggle SSE / Poll gate via admin endpoint</div>
          </div>
          <div style={{marginLeft:"auto",display:"flex",gap:8,alignItems:"center"}}>
            <Badge status={status} />
            <button onClick={readStatus} disabled={loading} style={btnStyle("#1f2937")}>{loading?"Checking…":"Refresh"}</button>
          </div>
        </header>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,padding:18}}
        >
          <Panel title="Status">
            <p style={{marginTop:4, color:ui.dim}}>Endpoint: <code style={codeStyle}>{ENDPOINT}</code></p>
            <p style={{marginTop:2, color:ui.dim}}>Probe: <code style={codeStyle}>{POLL_URL}</code></p>
            <div style={{marginTop:12, display:"flex", gap:12}}>
              <button onClick={() => sendAction("on")}  disabled={loading} style={btnBig(ui.brand2)}>
                {loading?"…":"Enable feed"}
              </button>
              <button onClick={() => sendAction("off")} disabled={loading} style={btnBig(ui.danger)}>
                {loading?"…":"Disable feed"}
              </button>
            </div>
            {note && (
              <div style={{marginTop:12, padding:12, borderRadius:10, background:"#171f2d", border:"1px solid "+ui.line, color: ui.dim}}>
                {note}
              </div>
            )}
          </Panel>

          <Panel title="Tips">
            <ul style={{margin:"6px 0 0 16px", color:ui.dim, lineHeight:1.5}}>
              <li>When <strong>OFF</strong>, <code style={codeStyle}>changes_stream.php</code> returns <code style={codeStyle}>410</code> with <code style={codeStyle}>retry: 0</code>.</li>
              <li>Clients should stop retrying on <code style={codeStyle}>410</code> and resume after you enable again.</li>
              <li>Use this panel before/after bulk admin edits to avoid background load.</li>
            </ul>
          </Panel>
        </div>

        <footer style={{padding:"12px 18px", borderTop:"1px solid "+ui.line, color:ui.dim, fontSize:12}}>
          Logged-in admin session is required. Requests are sent with <code style={codeStyle}>credentials: 'include'</code>.
        </footer>
      </div>
    </div>
  );
}

function Badge({ status }){
  const map = {
    on:   { label: "ENABLED", bg: "#064e3b" },
    off:  { label: "DISABLED", bg: "#7f1d1d" },
    error:{ label: "ERROR", bg: "#7c2d12" },
    unknown: { label: "UNKNOWN", bg: "#374151" },
  }[status || "unknown"];
  return (
    <span style={{background: map.bg, color:"#fff", fontWeight:800, padding:"6px 10px", borderRadius:12, fontSize:12, border:"1px solid rgba(255,255,255,.12)"}}>
      {map.label}
    </span>
  );
}

function Panel({ title, children }){
  return (
    <section style={{border:"1px solid rgba(255,255,255,.08)", borderRadius:12, padding:14, background:"#0f1726"}}>
      <div style={{fontWeight:900, fontSize:14, marginBottom:8}}>{title}</div>
      {children}
    </section>
  );
}

const codeStyle = { padding:"0 6px", background:"#0b1220", border:"1px solid rgba(255,255,255,.08)", borderRadius:6 };
const btnStyle = (bg) => ({ background: bg, color: "#e5ecff", border:"1px solid rgba(255,255,255,.12)", padding:"8px 12px", borderRadius:10, fontWeight:800, cursor:"pointer" });
const btnBig = (bg) => ({ ...btnStyle(bg), padding:"12px 16px", minWidth:160 });
